import React from "react";

const PDFPreview = () => {
  // Sample data to preview the PDF
  const sampleData = {
    businessLegalName: "ABC Construction LLC",
    businessDBA: "ABC Builders",
    ein: "12-3456789",
    businessStreet: "123 Main Street",
    businessCity: "Miami",
    businessState: "FL",
    businessZip: "33101",
    businessStartDate: "01-15-2020",
    entityType: "LLC",
    industry: "Construction",
    owner1FirstName: "John",
    owner1LastName: "Smith",
    owner1Ownership: "60",
    owner1SSN: "XXX-XX-1234",
    owner1DOB: "05-20-1985",
    owner1FICO: "720",
    owner1Email: "john@abcconstruction.com",
    owner1Phone: "(305) 555-1234",
    owner1Street: "456 Oak Avenue",
    owner1City: "Miami",
    owner1State: "FL",
    owner1Zip: "33102",
    hasSecondOwner: true,
    owner2FirstName: "Jane",
    owner2LastName: "Smith",
    owner2Ownership: "40",
    owner2SSN: "XXX-XX-5678",
    owner2DOB: "08-10-1987",
    owner2FICO: "695",
    owner2Email: "jane@abcconstruction.com",
    owner2Phone: "(305) 555-5678",
    owner2Street: "456 Oak Avenue",
    owner2City: "Miami",
    owner2State: "FL",
    owner2Zip: "33102",
    signatureDate: "02-04-2026",
  };

  const disclaimer = `By signing above, the undersigned business and business owner(s)/officer(s) (individually and collectively referred to as "you") hereby authorize Lloyd Funding Group Inc dba Lloyd Funding, along with its representatives, successors, assigns, and designees ("Recipients"), who may be involved in or acquire commercial financing products including daily repayment loans or purchases of future receivables such as Merchant Cash Advances (collectively, "Transactions"), to: (1) request and obtain consumer, personal, business, and investigative reports, as well as other relevant information about you, including credit inquiries (hard or soft pulls), from consumer reporting agencies including but not limited to TransUnion, Experian, and Equifax; (2) request and obtain credit card processing statements and bank statements from financial institutions, creditors, and other third parties; (3) share this application and any information gathered in connection with it to any or all Recipients for the purposes described herein; (4) authorize any creditor or financial institution to release information pertaining to you to Lloyd Funding Group Inc dba Lloyd Funding and to each of the Recipients; and (5) contact you by email, phone call, and/or text message at the contact information provided above, or at any email address or phone number reasonably associated with you, including mobile numbers (if applicable), even if registered on a Do-Not-Call list, using automated dialing systems or similar technology, regarding this application, future commercial financing opportunities, and/or other lawful marketing purposes. Consent to receive text messages is not required for application approval. I/We certify that all information provided in this application is complete, true, and accurate.`;

  const SectionHeader = ({ children }) => (
    <div
      style={{
        background: "#2563eb",
        color: "white",
        padding: "8px 16px",
        fontSize: "14px",
        fontWeight: "bold",
        marginTop: "20px",
        marginBottom: "12px",
      }}
    >
      {children}
    </div>
  );

  const Field = ({ label, value }) => (
    <div style={{ display: "flex", marginBottom: "8px", fontSize: "12px" }}>
      <div style={{ width: "150px", color: "#4b5563", fontWeight: "600" }}>
        {label}
      </div>
      <div style={{ color: "#1f2937" }}>{value || "N/A"}</div>
    </div>
  );

  return (
    <div className="py-8 px-4 bg-gray-100 min-h-screen">
      <div className="max-w-2xl mx-auto">
        {/* PDF Page */}
        <div
          style={{
            background: "white",
            padding: "40px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            fontFamily: "Helvetica, Arial, sans-serif",
            minHeight: "1056px", // Letter size aspect ratio
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              {/* Logo Icon */}
              <svg
                style={{ height: "32px", width: "auto" }}
                viewBox="-5 -5 160 130"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g fill="#2563EB">
                  <path d="M57,36.1l-22.6,41.8c-0.3,0.5,0.1,1.2,0.7,1.2h47.8l19,34.6H29.8c-23.9,0-39.2-25.3-28.2-46.5l16.1-31H57z" />
                  <path d="M84.6,78.1l22.6-41.8c0.3-0.5-0.1-1.2-0.7-1.2H58.7l-19-34.6h72.1c23.9,0,39.2,25.3,28.2,46.5l-16.1,31H84.6z" />
                </g>
              </svg>
              <h1
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1e3a5f",
                  margin: 0,
                  letterSpacing: "-0.025em",
                }}
              >
                Lloyd Funding
              </h1>
            </div>
          </div>

          {/* Business Information */}
          <SectionHeader>BUSINESS INFORMATION</SectionHeader>
          <Field label="Business Name:" value={sampleData.businessLegalName} />
          <Field label="DBA:" value={sampleData.businessDBA} />
          <Field label="EIN/Tax ID:" value={sampleData.ein} />
          <Field
            label="Address:"
            value={`${sampleData.businessStreet}, ${sampleData.businessCity}, ${sampleData.businessState} ${sampleData.businessZip}`}
          />
          <Field
            label="Business Start Date:"
            value={sampleData.businessStartDate}
          />
          <Field label="Entity Type:" value={sampleData.entityType} />
          <Field label="Industry:" value={sampleData.industry} />

          {/* Owner 1 Information */}
          <SectionHeader>OWNER / GUARANTOR INFORMATION</SectionHeader>
          <Field
            label="Full Name:"
            value={`${sampleData.owner1FirstName} ${sampleData.owner1LastName}`}
          />
          <Field label="Ownership:" value={`${sampleData.owner1Ownership}%`} />
          <Field label="SSN:" value={sampleData.owner1SSN} />
          <Field label="Date of Birth:" value={sampleData.owner1DOB} />
          <Field label="FICO Score:" value={sampleData.owner1FICO} />
          <Field label="Email:" value={sampleData.owner1Email} />
          <Field label="Phone:" value={sampleData.owner1Phone} />
          <Field
            label="Address:"
            value={`${sampleData.owner1Street}, ${sampleData.owner1City}, ${sampleData.owner1State} ${sampleData.owner1Zip}`}
          />

          {/* Owner 2 Information */}
          {sampleData.hasSecondOwner && (
            <>
              <SectionHeader>2ND OWNER / GUARANTOR INFORMATION</SectionHeader>
              <Field
                label="Full Name:"
                value={`${sampleData.owner2FirstName} ${sampleData.owner2LastName}`}
              />
              <Field
                label="Ownership:"
                value={`${sampleData.owner2Ownership}%`}
              />
              <Field label="SSN:" value={sampleData.owner2SSN} />
              <Field label="Date of Birth:" value={sampleData.owner2DOB} />
              <Field label="FICO Score:" value={sampleData.owner2FICO} />
              <Field label="Email:" value={sampleData.owner2Email} />
              <Field label="Phone:" value={sampleData.owner2Phone} />
              <Field
                label="Address:"
                value={`${sampleData.owner2Street}, ${sampleData.owner2City}, ${sampleData.owner2State} ${sampleData.owner2Zip}`}
              />
            </>
          )}

          {/* Signatures */}
          <SectionHeader>SIGNATURES</SectionHeader>
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                fontSize: "11px",
                color: "#4b5563",
                marginBottom: "4px",
              }}
            >
              Owner 1 Signature:
            </div>
            <div
              style={{
                border: "1px solid #d1d5db",
                height: "60px",
                background: "#f9fafb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9ca3af",
                fontSize: "12px",
                fontStyle: "italic",
              }}
            >
              [Signature Image]
            </div>
          </div>

          {sampleData.hasSecondOwner && (
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#4b5563",
                  marginBottom: "4px",
                }}
              >
                Owner 2 Signature:
              </div>
              <div
                style={{
                  border: "1px solid #d1d5db",
                  height: "60px",
                  background: "#f9fafb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9ca3af",
                  fontSize: "12px",
                  fontStyle: "italic",
                }}
              >
                [Signature Image]
              </div>
            </div>
          )}

          <Field label="Signature Date:" value={sampleData.signatureDate} />

          {/* Legal Disclaimer */}
          <SectionHeader>LEGAL DISCLAIMER</SectionHeader>
          <p
            style={{
              fontSize: "9px",
              color: "#6b7280",
              lineHeight: "1.5",
              textAlign: "justify",
            }}
          >
            {disclaimer}
          </p>

          {/* Footer */}
          <div
            style={{
              marginTop: "40px",
              paddingTop: "12px",
              borderTop: "1px solid #e5e7eb",
              textAlign: "center",
              fontSize: "9px",
              color: "#9ca3af",
            }}
          >
            Lloyd Funding - Confidential | Page 1 of 1
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPreview;
